import { lazy, Suspense, useState } from 'react'
import './index.css'
import { useEnergyStore } from '@/stores/useEnergyStore'
import { useAuth, useDarkModeInit, useNetworkStatus } from '@/hooks'
import { UserButton } from '@/components/auth/UserButton'
import { Sidebar } from '@/components/layout/Sidebar'
import { KpiSection } from '@/components/kpi/KpiSection'
import { DashboardCharts } from '@/components/charts/DashboardCharts'
import { PlanMatrix } from '@/components/matrix/PlanMatrix'
import { CollapsibleCard, Select } from '@/components/ui'
import { useSync } from '@/hooks/useSync'
import { 
  Sun, 
  Moon, 
  Wifi, 
  WifiOff, 
  Info, 
  Plus, 
  Table, 
  TrendingUp, 
  BarChart2, 
  Activity,
  ArrowUpDown,
  Library,
  Filter
} from 'lucide-react'

import { SettingsDropdown } from '@/components/layout/SettingsDropdown'
import { ErrorBoundary } from '@/components/common/ErrorBoundary'
import { KpiSkeleton, ChartSkeleton } from '@/components/ui/Skeleton'

import { ConfirmModal } from '@/components/modals/ConfirmModal'

const HouseholdModal = lazy(() => import('@/components/modals/HouseholdModal').then(m => ({ default: m.HouseholdModal })))
const HelpModal = lazy(() => import('@/components/modals/HelpModal').then(m => ({ default: m.HelpModal })))
const PlanModal = lazy(() => import('@/components/modals/PlanModal').then(m => ({ default: m.PlanModal })))
const AuthModal = lazy(() => import('@/components/auth/AuthModal').then(m => ({ default: m.AuthModal })))
const PortfolioModal = lazy(() => import('@/components/modals/PortfolioModal').then(m => ({ default: m.PortfolioModal })))
const PricingModal = lazy(() => import('@/components/modals/PricingModal').then(m => ({ default: m.PricingModal })))
const DailyEnergyFlowsModal = lazy(() => import('@/components/modals/DailyEnergyFlowsModal').then(m => ({ default: m.DailyEnergyFlowsModal })))
const SettingsModal = lazy(() => import('@/components/modals/SettingsModal').then(m => ({ default: m.SettingsModal })))
const KpiBreakdownModal = lazy(() => import('@/components/modals/KpiBreakdownModal').then(m => ({ default: m.KpiBreakdownModal })))

function App() {
  useAuth()
  useSync()
  useDarkModeInit()
  useNetworkStatus()

  const darkMode = useEnergyStore((s) => s.darkMode)
  const toggleDarkMode = useEnergyStore((s) => s.toggleDarkMode)
  const sidebarOpen = useEnergyStore((s) => s.sidebarOpen)
  const toggleSidebar = useEnergyStore((s) => s.toggleSidebar)
  
  const isAuthLoading = useEnergyStore((s) => s.isAuthLoading)
  
  const isOnline = useEnergyStore((s) => s.isOnline)
  const isHelpModalOpen = useEnergyStore((s) => s.isHelpModalOpen)
  const setHelpModalOpen = useEnergyStore((s) => s.setHelpModalOpen)
  const setPlanModalOpen = useEnergyStore((s) => s.setPlanModalOpen)
  
  const isHouseholdModalOpen = useEnergyStore((s) => s.isHouseholdModalOpen)
  const isPricingModalOpen = useEnergyStore((s) => s.isPricingModalOpen)
  const isDailyFlowsModalOpen = useEnergyStore((s) => s.isDailyFlowsModalOpen)
  const setDailyFlowsModalOpen = useEnergyStore((s) => s.setDailyFlowsModalOpen)
  const isAuthModalOpen = useEnergyStore((s) => s.isAuthModalOpen)
  const setAuthModalOpen = useEnergyStore((s) => s.setAuthModalOpen)
  const isSettingsModalOpen = useEnergyStore((s) => s.isSettingsModalOpen)
  const setSettingsModalOpen = useEnergyStore((s) => s.setSettingsModalOpen)
  const isPortfolioModalOpen = useEnergyStore((s) => s.isPortfolioModalOpen)
  const setPortfolioModalOpen = useEnergyStore((s) => s.setPortfolioModalOpen)
  const portfolios = useEnergyStore((s) => s.portfolios)
  const activePortfolioId = useEnergyStore((s) => s.activePortfolioId)
  const setActivePortfolioId = useEnergyStore((s) => s.setActivePortfolioId)

  const pendingSync = useEnergyStore((s) => s.pendingSync)
  const lastSyncedAt = useEnergyStore((s) => s.lastSyncedAt)
  const isAuthenticated = useEnergyStore((s) => s.isAuthenticated)

  const [isSorted, setIsSorted] = useState(false)
  const [showPortfolioTools, setShowPortfolioTools] = useState(false)

  return (
    <ErrorBoundary>
      <div
        className={`min-h-screen transition-colors duration-300 ${
          darkMode ? 'bg-slate-950 text-slate-200' : 'bg-[#f8fbff] text-slate-800'
        }`}
      >
        {/* Header */}
        <header
          className={`fixed top-0 left-0 right-0 z-50 border-b backdrop-blur-xl ${
            darkMode ? 'bg-slate-950/80 border-white/5' : 'bg-white/80 border-slate-200'
          }`}
        >
          {/* ... Header Content (keep as is) ... */}
          <div className="mx-auto px-4 h-14 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button
                onClick={toggleSidebar}
                className="w-8 h-8 rounded-lg overflow-hidden flex items-center justify-center bg-white shadow-sm ring-1 ring-slate-900/5 transition-all active:scale-95 hover:opacity-90 cursor-pointer"
              >
                <img src="/favicon.png" alt="Scenario Logo" className="w-full h-full object-cover" />
              </button>
              <h1 className="text-sm font-black uppercase tracking-widest hidden sm:block">
                Scenario
              </h1>
            </div>

            <div className="flex items-center gap-2">
              <div className="w-px h-4 mx-1 bg-slate-200 dark:bg-white/10" />

              {/* Online indicator & Sync status */}
              <div
                className={`hidden md:flex flex-col items-end gap-0.5 px-3 py-1.5 rounded-xl border transition-all ${
                  darkMode ? 'bg-white/5 border-white/5' : 'bg-slate-50 border-slate-200'
                }`}
              >
                <div className="flex items-center gap-1.5">
                  {isAuthenticated ? (
                    isOnline ? (
                      <Wifi className="w-3 h-3 text-emerald-500" />
                    ) : (
                      <WifiOff className="w-3 h-3 text-amber-500" />
                    )
                  ) : (
                    <div className={`w-2 h-2 rounded-full ${darkMode ? 'bg-slate-500' : 'bg-slate-400'}`} />
                  )}
                  <span
                    className={`text-[10px] font-black uppercase tracking-tight ${
                      darkMode ? 'text-slate-400' : 'text-slate-500'
                    }`}
                  >
                    {!isAuthenticated ? 'Local Storage' : isOnline ? (pendingSync ? 'Syncing...' : 'Cloud Synced') : 'Offline'}
                  </span>
                </div>
                {isAuthenticated && lastSyncedAt && (
                  <span className={`text-[8px] font-bold opacity-50 ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                    Last: {new Date(lastSyncedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </span>
                )}
              </div>

              <div className="w-px h-4 mx-1 bg-slate-200 dark:bg-white/10" />

              {/* Help button */}
              <button
                onClick={() => setHelpModalOpen(true)}
                className={`p-2 rounded-xl transition-all border cursor-pointer ${
                  darkMode
                    ? 'bg-white/5 border-white/5 text-slate-400 hover:text-white hover:border-white/10'
                    : 'bg-white border-slate-200 text-slate-500 hover:text-slate-900 hover:border-slate-300'
                }`}
              >
                <Info className="w-4 h-4" />
              </button>

              {/* Settings dropdown */}
              <SettingsDropdown />

              {/* Dark mode toggle */}
              <button
                onClick={toggleDarkMode}
                className={`p-2 rounded-xl transition-all border cursor-pointer ${
                  darkMode
                    ? 'bg-white/5 border-white/5 text-slate-400 hover:text-white hover:border-white/10'
                    : 'bg-white border-slate-200 text-slate-500 hover:text-slate-900 hover:border-slate-300'
                }`}
              >
                {darkMode ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
              </button>

              <div className="w-px h-4 mx-1 bg-slate-200 dark:bg-white/10" />

              {/* User menu (Always visible if not loading) */}
              {isAuthLoading ? (
                <div className="w-24 h-8 bg-slate-100 dark:bg-white/5 animate-pulse rounded-xl" />
              ) : (
                <UserButton />
              )}
            </div>
          </div>
        </header>

        {/* Mobile Sidebar Overlay */}
        {sidebarOpen && (
          <div
            className="fixed inset-0 z-30 bg-slate-900/20 backdrop-blur-sm md:hidden animate-in fade-in duration-300"
            onClick={() => useEnergyStore.setState({ sidebarOpen: false })}
          />
        )}

        {/* Sidebar */}
        <Sidebar />

        {/* Main content */}
        <main
          className={`transition-all duration-500 ease-in-out pt-20 pb-12 min-h-screen ${
            sidebarOpen ? 'md:ml-80' : 'ml-0'
          }`}
        >
          <div className="max-w-7xl mx-auto px-4 md:px-8 space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-1000">
            {isAuthLoading ? (
              <>
                <div className="h-64 bg-slate-100 dark:bg-white/5 animate-pulse rounded-[3rem]" />
                <KpiSkeleton />
                <ChartSkeleton />
              </>
            ) : (
              <>
                <CollapsibleCard
                  title="Provider Rankings"
                  subtitle="Compare configurations side-by-side"
                  icon={Table}
                  action={
                    <button
                      onClick={() => setShowPortfolioTools(!showPortfolioTools)}
                      className={`p-2 rounded-xl transition-all border ${
                        showPortfolioTools 
                          ? (darkMode ? 'bg-blue-500/20 text-blue-400 border-blue-500/30' : 'bg-blue-50 text-blue-600 border-blue-200')
                          : (darkMode ? 'bg-white/5 text-slate-400 border-white/5 hover:text-white hover:bg-white/10' : 'bg-white text-slate-500 border-slate-200 hover:text-slate-900 hover:bg-slate-50')
                      }`}
                      title="Filter and Sort Plans"
                    >
                      <Filter size={16} />
                    </button>
                  }
                  badge={
                    useEnergyStore.getState().config.lineLossFactor > 0 && (
                      <div className={`flex items-center gap-1.5 text-[9px] font-black px-2 py-0.5 rounded-full border ${
                        darkMode 
                          ? 'bg-emerald-500/10 border-emerald-500/20 text-emerald-400' 
                          : 'bg-emerald-50 border-emerald-100 text-emerald-700'
                      }`}>
                        <Activity size={10} />
                        Includes {useEnergyStore.getState().config.lineLossFactor}% Line Loss
                      </div>
                    )
                  }
                >
                  <div className="flex flex-col">
                    <div className={`transition-all duration-500 ease-in-out ${showPortfolioTools ? 'max-h-[500px] opacity-100 overflow-visible mb-3' : 'max-h-0 opacity-0 overflow-hidden mb-0'}`}>
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                        <div className="flex-1 w-full sm:max-w-xs flex items-center gap-2">
                          <div className="flex-1">
                            <Select
                              label=""
                              value={activePortfolioId}
                              options={[
                                { label: 'All Available Plans', value: 'all' },
                                ...portfolios.map(p => ({ label: p.name, value: p.id }))
                              ]}
                              onChange={setActivePortfolioId as any}
                              darkMode={darkMode}
                            />
                          </div>
                          <button
                            onClick={() => setPortfolioModalOpen(true)}
                            className={`p-3 rounded-xl border transition-colors cursor-pointer shrink-0 ${
                              darkMode ? 'bg-white/5 border-white/5 text-slate-400 hover:text-white' : 'bg-slate-50 border-slate-200 text-slate-500 hover:text-slate-900'
                            }`}
                            title="Manage Portfolios"
                          >
                            <Library size={20} />
                          </button>
                        </div>

                        <div className="grid grid-cols-2 sm:flex sm:justify-end gap-2 w-full sm:w-auto">
                          <button
                            onClick={() => setIsSorted(!isSorted)}
                            className={`flex items-center justify-center gap-1.5 w-full sm:w-auto px-4 py-3 sm:py-2 rounded-xl text-[10px] font-black uppercase transition-all shadow-sm active:scale-95 cursor-pointer border ${
                              isSorted
                                ? (darkMode ? 'bg-indigo-500/20 text-indigo-400 border-indigo-500/30' : 'bg-indigo-50 text-indigo-600 border-indigo-200')
                                : (darkMode ? 'bg-white/5 text-slate-400 border-white/5 hover:bg-white/10' : 'bg-white text-slate-500 border-slate-200 hover:bg-slate-50')
                            }`}
                          >
                            <ArrowUpDown size={14} /> {isSorted ? 'Sorted' : 'Sort'}
                          </button>
                          <button
                            onClick={() => setPlanModalOpen(true)}
                            className="flex items-center justify-center gap-1.5 w-full sm:w-auto bg-emerald-600 text-white px-4 py-3 sm:py-2 rounded-xl text-[10px] font-black uppercase hover:bg-emerald-700 transition-all shadow-sm active:scale-95 cursor-pointer shrink-0"
                          >
                            <Plus size={14} /> Add Scenario
                          </button>
                        </div>
                      </div>
                    </div>
                    
                    <PlanMatrix hideHeader isSorted={isSorted} />
                  </div>
                </CollapsibleCard>

                <CollapsibleCard
                  title="Economic Viability Summary"
                  subtitle="Financial impact and ROI projections"
                  icon={TrendingUp}
                  iconColor={darkMode ? 'bg-orange-500/10 text-orange-500' : 'bg-orange-50 text-orange-600'}
                >
                  <KpiSection hideHeader />
                </CollapsibleCard>

                <CollapsibleCard
                  title="Simulated Performance Analysis"
                  subtitle="Energy balance and monthly cost comparisons"
                  icon={BarChart2}
                  iconColor={darkMode ? 'bg-blue-500/10 text-blue-500' : 'bg-blue-50 text-blue-600'}
                >
                  <DashboardCharts hideHeader />
                </CollapsibleCard>
              </>
            )}
          </div>
        </main>

        {/* Modals */}
        <Suspense fallback={null}>
          <AuthModal isOpen={isAuthModalOpen} onClose={() => setAuthModalOpen(false)} />
          <HelpModal isOpen={isHelpModalOpen} onClose={() => setHelpModalOpen(false)} darkMode={darkMode} />
          <PlanModal />
          <PortfolioModal isOpen={isPortfolioModalOpen} onClose={() => setPortfolioModalOpen(false)} darkMode={darkMode} />
          <HouseholdModal isOpen={isHouseholdModalOpen} onClose={() => useEnergyStore.setState({ isHouseholdModalOpen: false })} darkMode={darkMode} />
          <PricingModal isOpen={isPricingModalOpen} onClose={() => useEnergyStore.setState({ isPricingModalOpen: false })} darkMode={darkMode} />
          <DailyEnergyFlowsModal isOpen={isDailyFlowsModalOpen} onClose={() => setDailyFlowsModalOpen(false)} darkMode={darkMode} />
          <SettingsModal isOpen={isSettingsModalOpen} onClose={() => setSettingsModalOpen(false)} darkMode={darkMode} />
          <KpiBreakdownModal />
        </Suspense>
        <ConfirmModal />
      </div>
    </ErrorBoundary>
  )
}

export default App
